import { Box, ButtonBase, Typography } from "@mui/material";
import { FormatIcon } from "./FormatIcon";

export interface FormatPickerItem<T extends string> {
  id: T;
  label: string;
  count: number | null;
  countText?: string;
  progress?: { current: number; required: number };
}

export interface FormatPickerProps<T extends string> {
  items: readonly FormatPickerItem<T>[];
  value: T | null;
  onChange: (value: T) => void;
  ariaLabel: string;
}

export const FormatPicker = <T extends string>({ items, value, onChange, ariaLabel }: FormatPickerProps<T>) => {
  const sortedItems = items
    .map((item, index) => ({ item, index }))
    .sort((left, right) => {
      if (left.item.id === "all") return 1;
      if (right.item.id === "all") return -1;
      if (left.item.count === null) return right.item.count === null ? left.index - right.index : 1;
      if (right.item.count === null) return -1;
      return right.item.count - left.item.count || left.index - right.index;
    })
    .map(({ item }) => item);
  return (
    <Box
      aria-label={ariaLabel}
      role="group"
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, minmax(0, 1fr))",
          lg: `repeat(${items.length}, minmax(0, 1fr))`,
        },
        gap: 1.25,
      }}
    >
      {sortedItems.map((item) => {
        const active = item.id === value;
        const count =
          item.countText ?? (item.count === null ? "Loading" : `${item.count} ${item.count === 1 ? "game" : "games"}`);
        return (
          <ButtonBase
            disableRipple
            key={item.id}
            aria-pressed={active}
            aria-label={`${item.label} ${count}`}
            onClick={() => onChange(item.id)}
            sx={{
              minHeight: 44,
              textAlign: "left",
              alignItems: "stretch",
              transition: "none",
              "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
            }}
          >
            <Box
              sx={{
                width: "100%",
                minHeight: { xs: 176, sm: 220, lg: 250 },
                p: { xs: 2, sm: 2.5 },
                border: "1px solid",
                borderColor: active ? "primary.main" : "divider",
                bgcolor: active ? "primary.main" : "background.paper",
                color: active ? "#0a0a0a" : "text.primary",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 2,
                transition: "none",
                "&:hover": { borderColor: "primary.main", bgcolor: active ? "primary.dark" : "action.hover" },
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <Typography
                  sx={{
                    textTransform: "uppercase",
                    letterSpacing: "0.035em",
                    fontSize: { xs: "1.35rem", sm: "1.75rem" },
                    fontWeight: 900,
                    lineHeight: 1,
                  }}
                >
                  {item.label}
                </Typography>
                <FormatIcon format={item.id} />
              </Box>
              <Box>
                <Typography
                  sx={{ fontWeight: 900, fontSize: { xs: "1rem", sm: "1.25rem" }, fontVariantNumeric: "tabular-nums" }}
                >
                  {count}
                </Typography>
                {item.progress && (
                  <Typography sx={{ mt: 0.25, fontSize: ".75rem", fontWeight: 800 }}>
                    {item.progress.current} / {item.progress.required}
                  </Typography>
                )}
              </Box>
            </Box>
          </ButtonBase>
        );
      })}
    </Box>
  );
};
